import { get } from 'lodash-es';
import { getModelState } from 'client/data/luckdragon/model';
import { buildMakeModelDefaultYearV2, VehicleModel } from 'client/data/models/vehicle-v2';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { AppraisalModel, buildAllStylesAppraisalPath } from 'client/data/models/appraisal';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { OG_TYPE_PRODUCT } from 'site-modules/shared/constants/og-types';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getAppraisalValues } from 'site-modules/shared/utils/appraisal/common-utils';
import { CmsModel } from 'client/data/models/cms';
import { APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH } from 'site-modules/shared/constants/appraiser-highlights';
import { getMeta } from 'site-modules/shared/utils/seo-helper';
import { parseContent } from 'client/data/cms/content';
import {
  getSeoImage,
  buildVehicleName,
  getFaqContent,
} from 'client/site-modules/appraiser/pages/appraiser/appraiser-utils';
import { DEFAULT_TITLE } from 'site-modules/shared/constants/seo';

export const fillContentStringWithMMY = (string = '', { make, model, year } = {}) =>
  string.replaceAll('{make}', make).replaceAll('{model}', model).replaceAll('{year}', year);

/**
 * Gets appraiser seo object.
 * @param {object} state
 * @param {object} props
 * @returns {object}
 */
export function getAppraiserHighlightsSeoObject(state, props) {
  const {
    location,
    match: { params },
  } = props;
  const { pathname } = location;
  const { make, model, year } = params;

  const modelState = getModelState(state);
  const defaultYear = modelState.get(buildMakeModelDefaultYearV2(params), VehicleModel);
  const vehicleStyleId = modelState.get('estimatedAppraisalStyle', VehicleModel);
  const estimatedVehicle = modelState.get(`styles.${vehicleStyleId}`, VehicleModel);
  const appraisalStyles = modelState.get(buildAllStylesAppraisalPath(params), AppraisalModel);

  const { minAppraisalValue, maxAppraisalValue } = getAppraisalValues({
    appraisalStyles,
    estimatedStyleId: vehicleStyleId,
  });
  const hasEstimatedPrices = minAppraisalValue && maxAppraisalValue;

  const imageUrl = estimatedVehicle && estimatedVehicle.photo;
  const vehicleTitle = buildVehicleName(estimatedVehicle);
  let title = `${vehicleTitle} Value - ${
    hasEstimatedPrices ? `${minAppraisalValue}-${maxAppraisalValue}` : "What's it Worth?"
  } | Edmunds`;

  const content = modelState.get(`content["${APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH}"]`, CmsModel);
  if (content) {
    title = getMeta(parseContent(content)).title;
    title = fillContentStringWithMMY(title, {
      make: get(estimatedVehicle, 'makeName'),
      model: get(estimatedVehicle, 'modelName'),
      year: get(estimatedVehicle, 'year'),
    });
  } else {
    title = DEFAULT_TITLE;
  }

  return {
    title,
    description:
      appraisalStyles && appraisalStyles.length > 1
        ? `How much is a ${vehicleTitle}? Edmunds provides free, instant appraisal values. Check the ${get(
            appraisalStyles,
            [0, 'styleName'],
            vehicleTitle
          )} price, the ${get(
            appraisalStyles,
            [1, 'styleName'],
            vehicleTitle
          )} price, or any other ${vehicleTitle} price with Edmunds car appraisal value calculator.`
        : `Get the value of your used ${vehicleTitle} based on options, mileage and condition - find your trade-in value, private party and dealer retail.`,
    keywords: `${vehicleTitle}, trade-in value, private party, Edmunds.com`,
    canonical: `${SITE_URL}/${make}/${model}/${year}/appraisal-value/`,
    image: getSeoImage(imageUrl),
    type: OG_TYPE_PRODUCT,
    jsonld: [
      getBreadcrumbSchema(
        buildCoreBreadcrumbs({
          makeModelSubmodelYear: estimatedVehicle && {
            make: { name: estimatedVehicle.makeName, slug: estimatedVehicle.makeSlug },
            model: { name: estimatedVehicle.modelName, slug: estimatedVehicle.modelSlug },
            submodels: estimatedVehicle.submodels,
            year: estimatedVehicle.year,
          },
          params,
          defaultYear,
          pageDisplayName: 'Appraisal value',
          includeLastLink: true,
          urlPath: getUrlPathFromParams(params, 'appraisal-value'),
        })
      ),
      getFAQSchema({
        entries: getFaqContent({
          appraisalStyles,
          estimatedStyleId: vehicleStyleId,
          estimatedVehicle,
          pathname,
        }),
      }),
    ],
    robots: { noIndex: true },
  };
}
